import React, { useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Header from '../../components/Header';
import { api, openUploadDownload, getApiErrorMessage } from '../../api/client';
import { putToPresignedUrl } from '../../api/upload';
import { updateQueryDataMapped } from '../../lib/queryClient';
// issued_date / expiry_date are calendar days. They were parsed locally and
// then rendered through a bare toLocaleDateString(), so a cert reading
// "Expires: 02/03/2026" meant February to one hand and March to the next —
// on the screen that decides whether somebody is allowed on the pad.
// formatCalendarDate pins a month name, which cannot be read two ways.
import { daysUntilCalendarDate, formatCalendarDate, parseCalendarDate, toCalendarDateString } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import {
  ArrowLeft,
  Award,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Camera,
  Eye,
  Loader2,
  Trash2,
  X,
  Plus,
  ScanLine,
  Sparkles,
  RefreshCw,
  CloudOff,
} from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { CertReviewBadge } from '../../components/CertReviewBadge';
import { CERT_TYPES, certTypeLabel } from '../../lib/certTypes';
import { useBack } from '../../hooks/useBack';

const isNative = Capacitor.isNativePlatform();

// ─── Scan types ──────────────────────────────────────────────────────────────
// Typed to the payload POST /employee/my-certifications/scan actually returns
// (verified against employee.routes.ts:1892), not to what it ought to return.
// The endpoint emits both casings; we read the camelCase side consistently
// rather than mixing, and send documented snake_case on the write.

type Confidence = 'high' | 'medium' | 'low';

/** Confidence is keyed camelCase by the service, with an `overall` alongside. */
interface CertScanConfidence {
  certificationType?: Confidence;
  customType?: Confidence;
  certificateNumber?: Confidence;
  issuingBody?: Confidence;
  issuedDate?: Confidence;
  expiryDate?: Confidence;
  overall?: Confidence;
}

interface CertScanData {
  certificationType: string;
  customType: string | null;
  /** What Claude read off the card before it was mapped onto the vocabulary. */
  readCertificationType: string | null;
  certificateNumber: string | null;
  issuingBody: string | null;
  issuedDate: string | null;
  expiryDate: string | null;
  confidence: CertScanConfidence;
  warnings: string[];
  matchedExisting: boolean;
  existingCertId: string | null;
  existingCert: {
    id: string;
    certificateNumber: string | null;
    expiryDate: string | null;
    reviewStatus: string | null;
  } | null;
}

/**
 * The three failure modes the endpoint deliberately keeps apart, plus the
 * client-side one. Collapsing them is the whole thing the API contract exists
 * to prevent: only `unreadable` is worth handing a camera back to a worker for.
 */
type ScanErrorKind = 'unreadable' | 'unavailable' | 'upload' | 'network';

interface ScanError {
  kind: ScanErrorKind;
  message: string;
}

/** Which confirm-sheet fields carry provenance. */
type ScanField =
  | 'certificationType'
  | 'customType'
  | 'certificateNumber'
  | 'issuingBody'
  | 'issuedDate'
  | 'expiryDate';

// `<input type="date">` needs a YYYY-MM-DD value; API dates may be full ISO strings.
// Going via toISOString() would hand back UTC's day, which is the day before the
// one on the cert for anything stamped after 18:00 Mountain.
function toDateInput(value: string | null | undefined): string {
  const d = parseCalendarDate(value);
  return d ? toCalendarDateString(d) : '';
}

/**
 * Label row for a confirm-sheet field: name, where the value came from, and
 * whether the scan was unsure about it.
 *
 * Provenance is not decoration. Every value in this sheet is a proposal from a
 * model reading a photograph, and the worker is about to sign their name to it
 * as a compliance record — "this came from the photo" and "you typed this" are
 * different claims and have to look different.
 */
function FieldLabel({
  children,
  source,
  confidence,
}: {
  children: React.ReactNode;
  source: 'scanned' | 'edited' | 'blank';
  confidence?: Confidence;
}) {
  const lowConfidence = source === 'scanned' && confidence === 'low';
  return (
    <div className="flex items-center gap-2 mb-1 flex-wrap">
      <label className="text-xs text-fieldiq-muted">{children}</label>
      {source === 'scanned' && (
        <span
          className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${
            lowConfidence
              ? 'bg-amber-500/20 text-amber-300'
              : 'bg-fieldiq-accent/20 text-fieldiq-accent'
          }`}
        >
          {lowConfidence ? <AlertTriangle size={10} /> : <Sparkles size={10} />}
          {lowConfidence ? 'Check this' : 'Scanned'}
        </span>
      )}
      {source === 'edited' && (
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-fieldiq-dark text-fieldiq-text border border-fieldiq-border">
          Edited
        </span>
      )}
    </div>
  );
}

/** Two type labels equal ignoring case, underscores, and punctuation. */
function sameLabel(a: string, b: string): boolean {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  return norm(a) === norm(b);
}

/** Amber ring on a field the scan was unsure of, so the eye lands there first. */
function fieldClass(source: 'scanned' | 'edited' | 'blank', confidence?: Confidence) {
  const base =
    'w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border outline-none text-white focus:border-fieldiq-accent';
  return source === 'scanned' && confidence === 'low'
    ? `${base} border-amber-500/60`
    : `${base} border-fieldiq-border`;
}

export default function FieldCertsScreen() {
  const goBack = useBack('/field');
  const queryClient = useQueryClient();
  // "Expires in N days" counts from the TENANT's today, not the phone's.
  const timeZone = useTenantTimeZone();
  const [uploading, setUploading] = useState<string | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeCertId, setActiveCertId] = useState<string | null>(null);

  // ─── Edit sheet state ──────────────────────────────────────────────────────
  const [editingCert, setEditingCert] = useState<any | null>(null);
  const [editCertNumber, setEditCertNumber] = useState('');
  const [editExpiry, setEditExpiry] = useState('');
  const [editIssued, setEditIssued] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const openEditSheet = (cert: any) => {
    setEditingCert(cert);
    setEditCertNumber(cert.certificate_number ?? cert.cert_number ?? '');
    setEditExpiry(toDateInput(cert.expiry_date));
    setEditIssued(toDateInput(cert.issued_date));
    setEditError(null);
  };

  const closeEditSheet = () => {
    setEditingCert(null);
    setEditError(null);
  };

  // ─── Add sheet state ───────────────────────────────────────────────────────
  const [adding, setAdding] = useState(false);
  const [addType, setAddType] = useState('');
  const [addCustomType, setAddCustomType] = useState('');
  const [addCertNumber, setAddCertNumber] = useState('');
  const [addIssued, setAddIssued] = useState('');
  const [addExpiry, setAddExpiry] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  // Set when the API returns a 409 DUPLICATE_CERT_TYPE — drives the "Edit existing" shortcut.
  const [addDuplicateCertId, setAddDuplicateCertId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // ─── Scan state ────────────────────────────────────────────────────────────
  const scanInputRef = useRef<HTMLInputElement>(null);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState<ScanError | null>(null);
  const [scanData, setScanData] = useState<CertScanData | null>(null);
  /**
   * The captured image, held client-side from capture until after the save.
   *
   * The scan endpoint persists nothing — it reads the photo and throws it
   * away. So the only copy of the ticket the worker photographed is this one,
   * and it has to survive the confirm sheet to be attached against the certId
   * the save returns. Dropping it would mean a scanned cert with no document.
   */
  const [scanPhoto, setScanPhoto] = useState<File | null>(null);
  const [scanSaving, setScanSaving] = useState(false);

  // Confirm-sheet form state
  const [scanType, setScanType] = useState('');
  const [scanCustomType, setScanCustomType] = useState('');
  const [scanCertNumber, setScanCertNumber] = useState('');
  const [scanIssuingBody, setScanIssuingBody] = useState('');
  const [scanIssued, setScanIssued] = useState('');
  const [scanExpiry, setScanExpiry] = useState('');
  const [scanFormError, setScanFormError] = useState<string | null>(null);
  /** Fields the worker has changed by hand — drives the Scanned/Edited chip. */
  const [scanTouched, setScanTouched] = useState<Set<ScanField>>(new Set());

  const markTouched = (field: ScanField) =>
    setScanTouched((prev) => (prev.has(field) ? prev : new Set(prev).add(field)));

  /** Provenance for one field: scan proposed it, worker typed it, or empty. */
  const sourceOf = (field: ScanField, value: string): 'scanned' | 'edited' | 'blank' => {
    if (scanTouched.has(field)) return 'edited';
    if (!value) return 'blank';
    const scanned = scanData
      ? ({
          certificationType: scanData.certificationType,
          customType: scanData.customType,
          certificateNumber: scanData.certificateNumber,
          issuingBody: scanData.issuingBody,
          issuedDate: scanData.issuedDate,
          expiryDate: scanData.expiryDate,
        }[field] ?? null)
      : null;
    return scanned ? 'scanned' : 'blank';
  };

  const closeScanSheet = () => {
    setScanData(null);
    setScanPhoto(null);
    setScanFormError(null);
    setScanTouched(new Set());
  };

  const openAddSheet = () => {
    setAddType('');
    setAddCustomType('');
    setAddCertNumber('');
    setAddIssued('');
    setAddExpiry('');
    setAddError(null);
    setAddDuplicateCertId(null);
    setAdding(true);
  };

  const closeAddSheet = () => {
    setAdding(false);
    setAddError(null);
    setAddDuplicateCertId(null);
  };

  const { data, isLoading } = useQuery({
    queryKey: ['field-my-certs'],
    queryFn: () => api.employee.myCertifications().then(r => r.data.data ?? r.data ?? []),
  });

  const certs: any[] = Array.isArray(data) ? data : [];

  // Types the tech already holds — excluded from the Add picklist ("other" stays open).
  //
  // Reads `certificationType`/`type`: GET /my-certifications returns those two
  // keys and neither `certification_type` nor `cert_type` (employee.routes.ts
  // :1580). Keying on the absent names made this set silently empty, so the
  // picklist excluded nothing and offering a duplicate type was the default —
  // the 409 handler below was catching what this was supposed to prevent.
  const takenTypes = new Set<string>(
    certs.map((c) => c.certificationType ?? c.type).filter(Boolean),
  );

  // ─── Scan confirm-sheet derived values ─────────────────────────────────────
  const isScanRenewal = !!scanData?.matchedExisting && !!scanData?.existingCertId;

  /** Display name for the scanned type — the custom name wins, as the list does. */
  const scanTypeLabel = scanData
    ? scanData.customType || certTypeLabel(scanData.certificationType)
    : '';

  /**
   * Options as (available ∪ currently-selected).
   *
   * A `<select>` cannot show a value it has no option for: it renders the
   * first option while state quietly holds the real one, so the sheet would
   * show the wrong certification and save it without ever looking wrong. The
   * scanned type is excluded by `takenTypes` in exactly the case where it
   * matters, so it is added back explicitly.
   */
  const scanTypeOptions = CERT_TYPES.filter(
    (t) => t.value === 'other' || t.value === scanType || !takenTypes.has(t.value),
  );

  const getExpiryInfo = (cert: any) => {
    const daysUntil = daysUntilCalendarDate(cert.expiry_date, timeZone);
    if (daysUntil == null) return { label: 'No expiry', color: 'text-fieldiq-muted', bg: 'bg-fieldiq-card', icon: CheckCircle };

    if (daysUntil < 0) return { label: `Expired ${Math.abs(daysUntil)}d ago`, color: 'text-red-400', bg: 'bg-red-500/20', icon: XCircle };
    if (daysUntil <= 30) return { label: `Expires in ${daysUntil}d`, color: 'text-amber-400', bg: 'bg-amber-500/20', icon: AlertTriangle };
    if (daysUntil <= 90) return { label: `Expires in ${daysUntil}d`, color: 'text-amber-400', bg: 'bg-amber-500/10', icon: AlertTriangle };
    return { label: 'Valid', color: 'text-green-400', bg: 'bg-green-500/20', icon: CheckCircle };
  };

  // ─── Scan handlers ───────────────────────────────────────────────────────────
  /**
   * Capture a ticket photo for scanning. Mirrors the document-upload capture
   * path (Capacitor Camera on native, hidden file input on web) rather than
   * introducing a second camera idiom on the same screen.
   *
   * Returns null when the worker cancelled, or when the web fallback is taking
   * over — the file input's change handler continues the flow there.
   */
  const captureForScan = async (): Promise<File | null> => {
    if (isNative) {
      try {
        const { Camera: CapCamera, CameraResultType, CameraSource } = await import('@capacitor/camera');
        const photo = await CapCamera.getPhoto({
          quality: 90,
          allowEditing: false,
          resultType: CameraResultType.Base64,
          source: CameraSource.Prompt,
          width: 1920,
          height: 1080,
        });
        if (!photo.base64String) return null;
        // The API accepts JPEG, PNG and WebP only, and re-encodes to JPEG
        // anyway. HEIC off an iPhone would 400, so normalise it here.
        const rawFormat = (photo.format || 'jpeg').toLowerCase();
        const format = rawFormat === 'heic' || rawFormat === 'heif' ? 'jpeg' : rawFormat;
        const mime = `image/${format === 'jpg' ? 'jpeg' : format}`;
        const blob = base64ToBlob(photo.base64String, mime);
        return new File([blob], `cert-scan-${Date.now()}.${format}`, { type: mime });
      } catch (err: any) {
        if (err?.message?.includes('cancelled') || err?.message?.includes('User cancelled')) return null;
        console.error('Camera error, falling back to file input:', err);
        // Fall through to the file input
      }
    }
    scanInputRef.current?.click();
    return null;
  };

  const handleScan = async () => {
    setScanError(null);
    const file = await captureForScan();
    if (file) await runScan(file);
  };

  const handleScanFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    await runScan(file);
  };

  /**
   * POST the photo and branch on the three documented outcomes.
   *
   * `scanCertification` resolves rather than throws on 400/502, so the split
   * happens here in one place: a 502 is Claude being down and must NOT read as
   * "your photo was bad", which is the entire reason the API separates them.
   */
  const runScan = async (file: File) => {
    setScanPhoto(file);
    setScanning(true);
    setScanError(null);
    try {
      const res = await api.employee.scanCertification(file, file.name);

      if (res.status === 502) {
        setScanPhoto(null);
        setScanError({
          kind: 'unavailable',
          message:
            res.data?.error ||
            'Certificate scanning is temporarily unavailable — please try again later.',
        });
        return;
      }
      if (res.status === 400) {
        setScanPhoto(null);
        setScanError({
          kind: 'upload',
          message: res.data?.error || 'That file could not be uploaded. Try a JPEG or PNG photo.',
        });
        return;
      }

      const body = res.data;
      // 200 + error:true — the photo was readable as a file but not as a
      // certificate. This is the one case worth offering a retake for.
      if (body?.error === true) {
        setScanPhoto(null);
        setScanError({
          kind: 'unreadable',
          message: body?.reason || 'Could not read the certificate — please retake the photo in good light.',
        });
        return;
      }

      // Envelope-aware unwrap. `data: null` on a success envelope would make
      // `body.data || body` hand back the envelope, whose every field reads
      // undefined — the form would then render blank and look scanned.
      const data: CertScanData | null = body && 'data' in body ? body.data : null;
      if (!data) {
        setScanPhoto(null);
        setScanError({
          kind: 'unreadable',
          message: 'The scan came back empty — please retake the photo in good light.',
        });
        return;
      }

      // Pre-fill. Every one of these stays editable; nothing saves until the
      // worker presses Save on the confirm sheet.
      setScanData(data);
      setScanTouched(new Set());
      setScanType(data.certificationType || '');
      setScanCustomType(data.customType || '');
      setScanCertNumber(data.certificateNumber || '');
      setScanIssuingBody(data.issuingBody || '');
      setScanIssued(toDateInput(data.issuedDate));
      setScanExpiry(toDateInput(data.expiryDate));
      setScanFormError(null);
    } catch (err: any) {
      // Anything left is transport: offline, DNS, timeout. Not a bad photo.
      console.error('Cert scan failed:', err);
      setScanPhoto(null);
      setScanError({
        kind: 'network',
        message: getApiErrorMessage(err, 'Could not reach the server. Check your signal and try again.'),
      });
    } finally {
      setScanning(false);
    }
  };

  // ─── Upload handler ──────────────────────────────────────────────────────────
  const handleUpload = async (certId: string) => {
    if (isNative) {
      // Use Capacitor Camera — fall back to file input if it fails
      try {
        const { Camera: CapCamera, CameraResultType, CameraSource } = await import('@capacitor/camera');
        const photo = await CapCamera.getPhoto({
          quality: 80,
          allowEditing: false,
          resultType: CameraResultType.Base64,
          source: CameraSource.Prompt,
          width: 1920,
          height: 1080,
        });
        if (!photo.base64String) return;
        const blob = base64ToBlob(photo.base64String, `image/${photo.format}`);
        const file = new File([blob], `cert-${Date.now()}.${photo.format}`, { type: `image/${photo.format}` });
        await uploadFile(certId, file);
        return;
      } catch (err: any) {
        if (err?.message?.includes('cancelled') || err?.message?.includes('User cancelled')) return;
        console.error('Camera error, falling back to file input:', err);
        // Fall through to file input
      }
    }
    // Web or native fallback: trigger file input
    setActiveCertId(certId);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeCertId) return;
    await uploadFile(activeCertId, file);
    e.target.value = '';
    setActiveCertId(null);
  };

  const uploadFile = async (certId: string, file: File) => {
    setUploading(certId);
    try {
      // iOS may report HEIC or empty content-type — normalise to jpeg
      let contentType = file.type || 'image/jpeg';
      if (contentType === 'image/heic' || contentType === 'image/heif') {
        contentType = 'image/jpeg';
      }

      // Step 1: Get presigned URL
      const res = await api.employee.uploadCertDocument(certId, {
        fileName: file.name,
        contentType,
      });
      const { uploadUrl, key } = res.data;

      // Step 2: Upload to S3. Throws unless S3 answered 2xx.
      await putToPresignedUrl(uploadUrl, file, contentType);

      // Step 3: Confirm
      await api.employee.confirmCertDocument(certId, {
        key,
        size: file.size,
      });

      // Refresh cert list
      queryClient.invalidateQueries({ queryKey: ['field-my-certs'] });
      toast.success('Document uploaded');
    } catch (err) {
      // Was a bare console.error. A hand holding a phone cannot read the
      // console: the spinner stopped, the card went back to "Upload Photo or
      // Document", and that is indistinguishable from never having tapped it.
      // Every other action on this screen already toasts its failure.
      console.error('Cert document upload failed:', err);
      toast.error(getApiErrorMessage(err, 'Upload failed. Please try again.'));
    } finally {
      setUploading(null);
    }
  };

  // ─── View handler ────────────────────────────────────────────────────────────
  const handleView = async (cert: any) => {
    const url = cert.document_url;
    if (!url) return;
    setViewing(cert.id);
    try {
      // document_url may be a bare UUID or a path like "/api/v1/uploads/download/<uuid>"
      const uuidRe = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
      const match = url.match(uuidRe);
      if (match) {
        await openUploadDownload(match[0]);
      } else {
        window.open(url, '_blank');
      }
    } catch (err) {
      console.error('View failed:', err);
    } finally {
      setViewing(null);
    }
  };

  // ─── Delete handler ──────────────────────────────────────────────────────────
  const handleDelete = async (certId: string) => {
    if (!window.confirm('Remove uploaded document?')) return;
    setDeleting(certId);
    try {
      await api.employee.deleteCertDocument(certId);
      queryClient.invalidateQueries({ queryKey: ['field-my-certs'] });
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeleting(null);
    }
  };

  // ─── Save edit handler ───────────────────────────────────────────────────────
  const handleSaveEdit = async () => {
    if (!editingCert) return;

    // Validation
    const certNumber = editCertNumber.trim();
    if (!certNumber) {
      setEditError('Certificate number is required.');
      return;
    }
    if (editIssued && editExpiry && editExpiry < editIssued) {
      setEditError('Expiry date must not be before the issued date.');
      return;
    }
    setEditError(null);

    // Build a body of only changed fields
    const body: { certificate_number?: string; expiry_date?: string; issued_date?: string } = {};
    const originalNumber = editingCert.certificate_number ?? editingCert.cert_number ?? '';
    if (certNumber !== originalNumber) body.certificate_number = certNumber;
    if (editExpiry !== toDateInput(editingCert.expiry_date)) body.expiry_date = editExpiry;
    if (editIssued !== toDateInput(editingCert.issued_date)) body.issued_date = editIssued;

    if (Object.keys(body).length === 0) {
      closeEditSheet();
      return;
    }

    const certId = editingCert.id;
    setSaving(true);
    try {
      const res = await api.employee.updateCertification(certId, body);
      if (res.data?.success === false) {
        throw new Error(res.data?.error || 'Failed to save changes.');
      }

      // Saving resets the cert to pending review server-side — reflect immediately.
      updateQueryDataMapped<any[]>(queryClient, ['field-my-certs'], (prev) =>
        prev?.map((c) =>
          c.id === certId
            ? {
                ...c,
                ...(body.certificate_number !== undefined
                  ? { certificate_number: body.certificate_number, cert_number: body.certificate_number }
                  : {}),
                ...(body.expiry_date !== undefined ? { expiry_date: body.expiry_date } : {}),
                ...(body.issued_date !== undefined ? { issued_date: body.issued_date } : {}),
                review_status: 'pending',
              }
            : c,
        ),
      );
      queryClient.invalidateQueries({ queryKey: ['field-my-certs'] });
      toast.success('Certification updated — sent for review');
      closeEditSheet();
    } catch (err: any) {
      const message =
        err?.response?.data?.error || err?.message || 'Failed to save changes. Please try again.';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  // ─── Create cert handler ─────────────────────────────────────────────────────
  const handleCreate = async () => {
    // Validation
    if (!addType) {
      setAddError('Certification type is required.');
      return;
    }
    const customType = addCustomType.trim();
    if (addType === 'other' && !customType) {
      setAddError('Custom type name is required.');
      return;
    }
    const certNumber = addCertNumber.trim();
    if (!certNumber) {
      setAddError('Certificate number is required.');
      return;
    }
    if (addIssued && addExpiry && addExpiry < addIssued) {
      setAddError('Expiry date must not be before the issued date.');
      return;
    }
    setAddError(null);
    setAddDuplicateCertId(null);

    setCreating(true);
    try {
      const res = await api.employee.createCertification({
        certification_type: addType,
        ...(addType === 'other' ? { custom_type: customType } : {}),
        certificate_number: certNumber,
        ...(addIssued ? { issued_date: addIssued } : {}),
        ...(addExpiry ? { expiry_date: addExpiry } : {}),
      });
      if (res.data?.success === false) {
        throw new Error(res.data?.error || 'Failed to add certification.');
      }

      queryClient.invalidateQueries({ queryKey: ['field-my-certs'] });
      toast.success('Certification added — sent for review');
      closeAddSheet();
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.error === 'DUPLICATE_CERT_TYPE') {
        // Show the API's message inline and offer a shortcut to edit the existing cert.
        setAddError(data.message || 'You already have this certification. Edit the existing one instead.');
        setAddDuplicateCertId(data.existingCertId ?? null);
        return;
      }
      const message =
        data?.error || err?.message || 'Failed to add certification. Please try again.';
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  /**
   * Save the confirmed scan, then attach the photo.
   *
   * Two write paths, chosen by `matchedExisting`:
   *
   *   RENEWAL  → PATCH the existing cert. `idx_employee_certs_unique_type`
   *              allows one cert per type per person, so a create here is a
   *              guaranteed 409. The type is not editable on this path —
   *              PATCH cannot change it, and changing it would mean a
   *              different certification entirely.
   *   NEW      → POST, as manual entry does.
   *
   * Both use the existing endpoints unchanged. Nothing the model produced
   * reaches a compliance record without the worker pressing this button.
   */
  const handleSaveScan = async () => {
    if (!scanData) return;
    const isRenewal = scanData.matchedExisting && !!scanData.existingCertId;

    if (!isRenewal && !scanType) {
      setScanFormError('Certification type is required.');
      return;
    }
    const customType = scanCustomType.trim();
    if (!isRenewal && scanType === 'other' && !customType) {
      setScanFormError('Custom type name is required.');
      return;
    }
    const certNumber = scanCertNumber.trim();
    if (!certNumber) {
      setScanFormError('Certificate number is required — check the card and type it in.');
      return;
    }
    if (scanIssued && scanExpiry && scanExpiry < scanIssued) {
      setScanFormError('Expiry date must not be before the issued date.');
      return;
    }
    setScanFormError(null);

    const issuingBody = scanIssuingBody.trim();
    // Held in a local: the photo has to survive the state reset below to be
    // attached, and `scanPhoto` is cleared when the sheet closes.
    const photo = scanPhoto;

    setScanSaving(true);
    try {
      let certId: string;

      if (isRenewal) {
        const res = await api.employee.updateCertification(scanData.existingCertId!, {
          certificate_number: certNumber,
          ...(scanIssued ? { issued_date: scanIssued } : {}),
          ...(scanExpiry ? { expiry_date: scanExpiry } : {}),
          ...(issuingBody ? { issuing_body: issuingBody } : {}),
        });
        if (res.data?.success === false) {
          throw new Error(res.data?.error || 'Failed to update certification.');
        }
        certId = scanData.existingCertId!;
      } else {
        const res = await api.employee.createCertification({
          certification_type: scanType,
          ...(scanType === 'other' ? { custom_type: customType } : {}),
          certificate_number: certNumber,
          ...(scanIssued ? { issued_date: scanIssued } : {}),
          ...(scanExpiry ? { expiry_date: scanExpiry } : {}),
          ...(issuingBody ? { issuing_body: issuingBody } : {}),
        });
        if (res.data?.success === false) {
          throw new Error(res.data?.error || 'Failed to add certification.');
        }
        const created = res.data?.data;
        if (!created?.id) {
          // The row saved; we just cannot key the photo to it. Say so rather
          // than reporting a clean success and dropping the ticket photo.
          queryClient.invalidateQueries({ queryKey: ['field-my-certs'] });
          closeScanSheet();
          toast.error('Certification saved, but the photo could not be attached. Add it from the card.');
          return;
        }
        certId = created.id;
      }

      queryClient.invalidateQueries({ queryKey: ['field-my-certs'] });
      toast.success(
        isRenewal
          ? 'Certification updated — sent for review'
          : 'Certification added — sent for review',
      );
      closeScanSheet();

      // Attach the captured photo against the saved certId, through the same
      // presign → PUT → confirm path the manual upload button uses. It toasts
      // its own failure, and the cert is already saved by this point.
      if (photo) await uploadFile(certId, photo);
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.error === 'DUPLICATE_CERT_TYPE') {
        // Belt and braces: the scan's renewal match should have caught this,
        // but the cert list can have moved under us since the scan.
        setScanFormError(
          data.message || 'You already have this certification. Close this and edit the existing one.',
        );
        return;
      }
      setScanFormError(
        data?.error || err?.message || 'Failed to save. Please try again.',
      );
    } finally {
      setScanSaving(false);
    }
  };

  // Close the Add sheet and open the Edit sheet for the duplicate cert (from a 409).
  const openEditForDuplicate = () => {
    const existing = addDuplicateCertId
      ? certs.find((c) => c.id === addDuplicateCertId)
      : null;
    closeAddSheet();
    if (existing) openEditSheet(existing);
  };

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <div className="max-w-[1920px] mx-auto">
        <Header />
        <div className="px-4 py-3 border-b border-fieldiq-border flex items-center gap-3">
          <button onClick={goBack} aria-label="Back" className="text-fieldiq-text">
            <ArrowLeft size={26} />
          </button>
          <h1 className="text-xl font-bold text-white">My Certifications</h1>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={handleScan}
              disabled={scanning}
              className="flex items-center gap-1.5 bg-fieldiq-card border border-fieldiq-border text-fieldiq-accent font-semibold rounded-lg py-2 px-3 text-sm disabled:opacity-50"
            >
              {scanning ? <Loader2 size={16} className="animate-spin" /> : <ScanLine size={16} />}
              Scan
            </button>
            <button
              onClick={openAddSheet}
              className="flex items-center gap-1.5 bg-fieldiq-accent text-fieldiq-dark font-semibold rounded-lg py-2 px-3 text-sm"
            >
              <Plus size={16} /> Add
            </button>
          </div>
        </div>
      </div>

      {/* Hidden file input for web uploads */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={handleFileSelected}
      />

      {/* Hidden file input for the web scan fallback.
          Images only — the scan endpoint takes JPEG, PNG or WebP, so offering
          a PDF here would earn a 400 after the worker picked one. */}
      <input
        ref={scanInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        className="hidden"
        data-testid="cert-scan-input"
        onChange={handleScanFileSelected}
      />

      <div className="max-w-[1920px] mx-auto px-4 py-4">
        {scanning && (
          <div className="mb-4 flex items-center gap-3 bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
            <Loader2 size={20} className="animate-spin text-fieldiq-accent shrink-0" />
            <div>
              <p className="text-sm font-semibold text-white">Reading your ticket…</p>
              <p className="text-xs text-fieldiq-muted mt-0.5">
                You'll get a chance to check every field before anything saves.
              </p>
            </div>
          </div>
        )}

        {/* The three error states, kept visually and verbally distinct.
            The API separates "bad photo" from "service down" precisely so this
            banner does not tell a worker to retake a photo that was fine. */}
        {scanError && !scanning && (
          <div
            className={`mb-4 rounded-xl p-4 border ${
              scanError.kind === 'unreadable'
                ? 'bg-amber-500/10 border-amber-500/40'
                : 'bg-red-500/10 border-red-500/40'
            }`}
          >
            <div className="flex items-start gap-3">
              {scanError.kind === 'unreadable' ? (
                <Camera size={20} className="text-amber-400 shrink-0 mt-0.5" />
              ) : scanError.kind === 'unavailable' ? (
                <CloudOff size={20} className="text-red-400 shrink-0 mt-0.5" />
              ) : (
                <AlertTriangle size={20} className="text-red-400 shrink-0 mt-0.5" />
              )}
              <div className="min-w-0 flex-1">
                <p
                  className={`text-sm font-semibold ${
                    scanError.kind === 'unreadable' ? 'text-amber-300' : 'text-red-300'
                  }`}
                >
                  {scanError.kind === 'unreadable'
                    ? "Couldn't read that photo"
                    : scanError.kind === 'unavailable'
                      ? 'Scanning is unavailable right now'
                      : scanError.kind === 'upload'
                        ? "That file wasn't accepted"
                        : "Couldn't reach the server"}
                </p>
                <p className="text-xs text-fieldiq-text mt-1">{scanError.message}</p>

                <div className="flex items-center gap-3 mt-3 flex-wrap">
                  {/* Only an unreadable photo is worth a retake. A 502 means
                      Claude is down — retaking the same card changes nothing,
                      so that path offers a plain retry and manual entry. */}
                  {scanError.kind === 'unreadable' || scanError.kind === 'upload' ? (
                    <button
                      onClick={handleScan}
                      className="flex items-center gap-1.5 bg-fieldiq-accent text-fieldiq-dark font-semibold rounded-lg py-2 px-3 text-xs"
                    >
                      <Camera size={14} /> Retake photo
                    </button>
                  ) : (
                    <button
                      onClick={handleScan}
                      className="flex items-center gap-1.5 bg-fieldiq-accent text-fieldiq-dark font-semibold rounded-lg py-2 px-3 text-xs"
                    >
                      <RefreshCw size={14} /> Try again
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setScanError(null);
                      openAddSheet();
                    }}
                    className="text-fieldiq-accent text-xs font-semibold underline underline-offset-2"
                  >
                    Enter it by hand
                  </button>
                  <button
                    onClick={() => setScanError(null)}
                    className="text-fieldiq-muted text-xs font-medium ml-auto"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-12 text-fieldiq-text">Loading certifications...</div>
        ) : certs.length === 0 ? (
          <div className="text-center py-12 text-fieldiq-text">
            <Award size={40} className="mx-auto mb-3 text-fieldiq-muted" />
            <p>No certifications on file</p>
          </div>
        ) : (
          <div className="space-y-3">
            {certs.map((cert: any) => {
              const expiry = getExpiryInfo(cert);
              const ExpiryIcon = expiry.icon;
              const isUploading = uploading === cert.id;
              const isViewing = viewing === cert.id;
              const isDeleting = deleting === cert.id;

              return (
                <div
                  key={cert.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openEditSheet(cert)}
                  className={`${expiry.bg} rounded-xl p-4 cursor-pointer`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <Award size={24} className={expiry.color} />
                      <div>
                        <h3 className="font-semibold">{cert.name}</h3>
                        {cert.cert_number && (
                          <p className="text-xs text-fieldiq-muted mt-0.5">#{cert.cert_number}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-fieldiq-muted">
                          {cert.issued_date && <span>Issued: {formatCalendarDate(cert.issued_date) ?? '—'}</span>}
                          {cert.expiry_date && <span>Expires: {formatCalendarDate(cert.expiry_date) ?? '—'}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <CertReviewBadge status={cert.review_status} />
                      <div className="flex items-center gap-1">
                        <ExpiryIcon size={16} className={expiry.color} />
                        <span className={`text-xs font-medium ${expiry.color}`}>{expiry.label}</span>
                      </div>
                    </div>
                  </div>

                  {/* Document actions — must not bubble up to the card's edit handler */}
                  <div className="mt-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    {cert.document_url ? (
                      <>
                        <button
                          onClick={() => handleView(cert)}
                          disabled={isViewing}
                          className="flex-1 flex items-center justify-center gap-2 bg-fieldiq-dark/60 text-green-400 py-2 rounded-lg text-xs font-medium disabled:opacity-50"
                        >
                          {isViewing ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                          View Document
                        </button>
                        <button
                          onClick={() => handleUpload(cert.id)}
                          disabled={isUploading}
                          className="flex items-center justify-center gap-1.5 bg-fieldiq-dark/60 text-fieldiq-accent py-2 px-3 rounded-lg text-xs font-medium disabled:opacity-50"
                        >
                          {isUploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
                          Replace
                        </button>
                        <button
                          onClick={() => handleDelete(cert.id)}
                          disabled={isDeleting}
                          className="flex items-center justify-center bg-fieldiq-dark/60 text-red-400 py-2 px-2.5 rounded-lg disabled:opacity-50"
                        >
                          {isDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleUpload(cert.id)}
                        disabled={isUploading}
                        className="flex-1 flex items-center justify-center gap-2 bg-fieldiq-dark/60 border border-dashed border-fieldiq-border text-fieldiq-text py-2.5 rounded-lg text-xs font-medium hover:border-fieldiq-accent hover:text-fieldiq-accent transition-colors disabled:opacity-50"
                      >
                        {isUploading ? (
                          <><Loader2 size={14} className="animate-spin" /> Uploading...</>
                        ) : (
                          <><Camera size={14} /> Upload Photo or Document</>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit cert sheet — matches the field-app centered modal pattern (FieldEquipmentScreen) */}
      {editingCert && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={closeEditSheet}
        >
          <div
            className="bg-fieldiq-card rounded-xl p-5 w-full max-w-md space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-white">Edit Certification</h3>
              <button onClick={closeEditSheet} className="text-fieldiq-muted">
                <X size={18} />
              </button>
            </div>

            {/* Read-only context */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <Award size={20} className="text-fieldiq-accent shrink-0" />
                <span className="font-semibold text-white truncate">{editingCert.name}</span>
              </div>
              <CertReviewBadge status={editingCert.review_status} />
            </div>

            <div>
              <label className="text-xs text-fieldiq-muted mb-1 block">Certificate Number *</label>
              <input
                type="text"
                value={editCertNumber}
                onChange={(e) => setEditCertNumber(e.target.value)}
                placeholder="e.g. ABC-12345"
                className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white"
              />
            </div>

            <div>
              <label className="text-xs text-fieldiq-muted mb-1 block">Issued Date</label>
              <input
                type="date"
                value={editIssued}
                onChange={(e) => setEditIssued(e.target.value)}
                className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white"
              />
            </div>

            <div>
              <label className="text-xs text-fieldiq-muted mb-1 block">Expiry Date</label>
              <input
                type="date"
                value={editExpiry}
                onChange={(e) => setEditExpiry(e.target.value)}
                className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white"
              />
            </div>

            <p className="text-xs text-fieldiq-muted">
              Saving changes will send this certification back for review.
            </p>

            {editError && <p className="text-red-400 text-xs">{editError}</p>}

            <div className="flex items-center gap-2">
              <button
                onClick={closeEditSheet}
                disabled={saving}
                className="flex-1 bg-fieldiq-dark/60 text-fieldiq-text font-semibold rounded-lg py-2.5 text-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving}
                className="flex-1 bg-fieldiq-accent text-fieldiq-dark font-semibold rounded-lg py-2.5 text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving && <Loader2 size={16} className="animate-spin" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add cert sheet — matches the Edit sheet's centered modal pattern */}
      {adding && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={closeAddSheet}
        >
          <div
            className="bg-fieldiq-card rounded-xl p-5 w-full max-w-md space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-white">Add Certification</h3>
              <button onClick={closeAddSheet} className="text-fieldiq-muted">
                <X size={18} />
              </button>
            </div>

            <div>
              <label className="text-xs text-fieldiq-muted mb-1 block">Certification Type *</label>
              <select
                value={addType}
                onChange={(e) => setAddType(e.target.value)}
                className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white"
              >
                <option value="" disabled>Select a type…</option>
                {CERT_TYPES
                  .filter((t) => t.value === 'other' || !takenTypes.has(t.value))
                  .map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
              </select>
            </div>

            {addType === 'other' && (
              <div>
                <label className="text-xs text-fieldiq-muted mb-1 block">Custom Type Name *</label>
                <input
                  type="text"
                  value={addCustomType}
                  onChange={(e) => setAddCustomType(e.target.value)}
                  placeholder="e.g. Confined Space Entry"
                  className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white"
                />
              </div>
            )}

            <div>
              <label className="text-xs text-fieldiq-muted mb-1 block">Certificate Number *</label>
              <input
                type="text"
                value={addCertNumber}
                onChange={(e) => setAddCertNumber(e.target.value)}
                placeholder="e.g. ABC-12345"
                className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white"
              />
            </div>

            <div>
              <label className="text-xs text-fieldiq-muted mb-1 block">Issued Date</label>
              <input
                type="date"
                value={addIssued}
                onChange={(e) => setAddIssued(e.target.value)}
                className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white"
              />
            </div>

            <div>
              <label className="text-xs text-fieldiq-muted mb-1 block">Expiry Date</label>
              <input
                type="date"
                value={addExpiry}
                onChange={(e) => setAddExpiry(e.target.value)}
                className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white"
              />
            </div>

            <p className="text-xs text-fieldiq-muted">
              Submitting will send this certification for review.
            </p>

            {addError && (
              <div className="space-y-2">
                <p className="text-red-400 text-xs">{addError}</p>
                {addDuplicateCertId && certs.some((c) => c.id === addDuplicateCertId) && (
                  <button
                    type="button"
                    onClick={openEditForDuplicate}
                    className="text-fieldiq-accent text-xs font-semibold underline underline-offset-2"
                  >
                    Edit existing certification
                  </button>
                )}
              </div>
            )}

            <div className="flex items-center gap-2">
              <button
                onClick={closeAddSheet}
                disabled={creating}
                className="flex-1 bg-fieldiq-dark/60 text-fieldiq-text font-semibold rounded-lg py-2.5 text-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="flex-1 bg-fieldiq-accent text-fieldiq-dark font-semibold rounded-lg py-2.5 text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {creating && <Loader2 size={16} className="animate-spin" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Scan confirm sheet — nothing here has been saved yet */}
      {scanData && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-fieldiq-card rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ScanLine size={18} className="text-fieldiq-accent" />
                  <h3 className="font-bold text-white">
                    {isScanRenewal ? 'Renew Certification' : 'Check Scanned Ticket'}
                  </h3>
                </div>
                <button onClick={closeScanSheet} disabled={scanSaving} className="text-fieldiq-muted disabled:opacity-50">
                  <X size={18} />
                </button>
              </div>

              {/* RENEWAL — say plainly that this replaces an existing ticket.
                  One cert per type per person, so this is an update, not a new
                  row, and a worker who thinks otherwise will go looking for a
                  second card that will never appear. */}
              {isScanRenewal ? (
                <div className="bg-fieldiq-accent/10 border border-fieldiq-accent/40 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <RefreshCw size={16} className="text-fieldiq-accent shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-fieldiq-accent">
                        Updating your existing {scanTypeLabel} ticket
                      </p>
                      <p className="text-xs text-fieldiq-text mt-1">
                        You already have this certification on file, so this replaces its details
                        rather than adding a second one. It goes back for review once saved.
                      </p>
                      {scanData.existingCert && (
                        <div className="mt-2 text-xs text-fieldiq-muted space-y-0.5">
                          <p>
                            Currently on file:{' '}
                            {scanData.existingCert.certificateNumber
                              ? `#${scanData.existingCert.certificateNumber}`
                              : 'no certificate number'}
                          </p>
                          <p>
                            Expires:{' '}
                            {formatCalendarDate(scanData.existingCert.expiryDate) ?? '—'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-fieldiq-muted">
                  These values were read off your photo. Check every one against the card — nothing
                  is saved until you press Save.
                </p>
              )}

              {/* Warnings the scan itself raised (unreadable date, expiry
                  before issue, existing cert). Server-authored and actionable. */}
              {scanData.warnings?.length > 0 && (
                <ul className="space-y-1">
                  {scanData.warnings.map((w, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-amber-300">
                      <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              )}

              {/* Overall confidence — a low read means check everything, not
                  just the flagged fields. */}
              {scanData.confidence?.overall && scanData.confidence.overall !== 'high' && (
                <p className="text-xs text-amber-300 flex items-center gap-1.5">
                  <AlertTriangle size={12} />
                  {scanData.confidence.overall === 'low'
                    ? 'This card was hard to read — check every field carefully.'
                    : 'Parts of this card were hard to read — check the flagged fields.'}
                </p>
              )}

              {/* Certification type. Fixed on a renewal: PATCH cannot change it,
                  and a different type is a different certification. */}
              <div>
                <FieldLabel
                  source={sourceOf('certificationType', scanType)}
                  confidence={scanData.confidence?.certificationType}
                >
                  Certification Type *
                </FieldLabel>
                {isScanRenewal ? (
                  <div className="w-full bg-fieldiq-dark/60 rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border text-fieldiq-text">
                    {scanTypeLabel}
                  </div>
                ) : (
                  <select
                    value={scanType}
                    onChange={(e) => {
                      setScanType(e.target.value);
                      markTouched('certificationType');
                    }}
                    className={fieldClass(
                      sourceOf('certificationType', scanType),
                      scanData.confidence?.certificationType,
                    )}
                  >
                    <option value="" disabled>Select a type…</option>
                    {scanTypeOptions.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                )}
                {/* What Claude actually read, when it differs from the mapped
                    value — so a wrong mapping is visible rather than silent.
                    Compared on alphanumerics only: the model often returns the
                    enum name ("H2S_alive") rather than the card's wording, and
                    showing that back as if it were a discrepancy is noise. */}
                {scanData.readCertificationType &&
                  !sameLabel(scanData.readCertificationType, scanTypeLabel) &&
                  !sameLabel(scanData.readCertificationType, scanData.certificationType) && (
                    <p className="text-xs text-fieldiq-muted mt-1">
                      Read from the card: “{scanData.readCertificationType}”
                    </p>
                  )}
              </div>

              {!isScanRenewal && scanType === 'other' && (
                <div>
                  <FieldLabel
                    source={sourceOf('customType', scanCustomType)}
                    confidence={scanData.confidence?.customType}
                  >
                    Custom Type Name *
                  </FieldLabel>
                  <input
                    type="text"
                    value={scanCustomType}
                    onChange={(e) => {
                      setScanCustomType(e.target.value);
                      markTouched('customType');
                    }}
                    placeholder="e.g. Rigging & Hoisting"
                    className={fieldClass(
                      sourceOf('customType', scanCustomType),
                      scanData.confidence?.customType,
                    )}
                  />
                </div>
              )}

              <div>
                <FieldLabel
                  source={sourceOf('certificateNumber', scanCertNumber)}
                  confidence={scanData.confidence?.certificateNumber}
                >
                  Certificate Number *
                </FieldLabel>
                <input
                  type="text"
                  value={scanCertNumber}
                  onChange={(e) => {
                    setScanCertNumber(e.target.value);
                    markTouched('certificateNumber');
                  }}
                  placeholder="e.g. ABC-12345"
                  className={fieldClass(
                    sourceOf('certificateNumber', scanCertNumber),
                    scanData.confidence?.certificateNumber,
                  )}
                />
              </div>

              <div>
                <FieldLabel
                  source={sourceOf('issuingBody', scanIssuingBody)}
                  confidence={scanData.confidence?.issuingBody}
                >
                  Issuing Body
                </FieldLabel>
                <input
                  type="text"
                  value={scanIssuingBody}
                  onChange={(e) => {
                    setScanIssuingBody(e.target.value);
                    markTouched('issuingBody');
                  }}
                  placeholder="e.g. Energy Safety Canada"
                  className={fieldClass(
                    sourceOf('issuingBody', scanIssuingBody),
                    scanData.confidence?.issuingBody,
                  )}
                />
              </div>

              <div>
                <FieldLabel
                  source={sourceOf('issuedDate', scanIssued)}
                  confidence={scanData.confidence?.issuedDate}
                >
                  Issued Date
                </FieldLabel>
                <input
                  type="date"
                  value={scanIssued}
                  onChange={(e) => {
                    setScanIssued(e.target.value);
                    markTouched('issuedDate');
                  }}
                  className={fieldClass(sourceOf('issuedDate', scanIssued), scanData.confidence?.issuedDate)}
                />
              </div>

              <div>
                <FieldLabel
                  source={sourceOf('expiryDate', scanExpiry)}
                  confidence={scanData.confidence?.expiryDate}
                >
                  Expiry Date
                </FieldLabel>
                <input
                  type="date"
                  value={scanExpiry}
                  onChange={(e) => {
                    setScanExpiry(e.target.value);
                    markTouched('expiryDate');
                  }}
                  className={fieldClass(sourceOf('expiryDate', scanExpiry), scanData.confidence?.expiryDate)}
                />
              </div>

              <p className="text-xs text-fieldiq-muted">
                {scanPhoto
                  ? 'Your photo will be attached to this certification and sent for review.'
                  : 'Submitting will send this certification for review.'}
              </p>

              {scanFormError && <p className="text-red-400 text-xs">{scanFormError}</p>}

              <div className="flex items-center gap-2">
                <button
                  onClick={closeScanSheet}
                  disabled={scanSaving}
                  className="flex-1 bg-fieldiq-dark/60 text-fieldiq-text font-semibold rounded-lg py-2.5 text-sm disabled:opacity-50"
                >
                  Discard
                </button>
                <button
                  onClick={handleSaveScan}
                  disabled={scanSaving}
                  className="flex-1 bg-fieldiq-accent text-fieldiq-dark font-semibold rounded-lg py-2.5 text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {scanSaving && <Loader2 size={16} className="animate-spin" />}
                  {isScanRenewal ? 'Update Ticket' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function base64ToBlob(base64: string, mimeType: string): Blob {
  const byteChars = atob(base64);
  const byteArrays: Uint8Array[] = [];
  for (let offset = 0; offset < byteChars.length; offset += 512) {
    const slice = byteChars.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    byteArrays.push(new Uint8Array(byteNumbers));
  }
  return new Blob(byteArrays, { type: mimeType });
}
